import type { ReactNode } from 'react'
import { Fragment } from 'react'

export type InferVariables<S extends string> =
  S extends `${string}{${infer K}}${infer Z}` ? K | InferVariables<Z> : never

export function interpolate<VALUE extends string>(
  ...args: InferVariables<VALUE> extends never
    ? [value: VALUE]
    : [value: VALUE, variables: Record<InferVariables<VALUE>, string>]
): string
export function interpolate<VALUE extends string>(
  ...args: InferVariables<VALUE> extends never
    ? [value: VALUE]
    : [value: VALUE, variables: Record<InferVariables<VALUE>, ReactNode>]
): ReactNode
export function interpolate(
  value: string,
  variables?: Record<string, string | ReactNode>
): string | ReactNode {
  // Convert string into array of parts; variables as like `['variable']`.
  const fragments = value
    .split(/{/)
    // -> ['A ', 'string} with ', 'variables} and ', 'trailing} words.']
    .map(($) => $.split(/}/))
    // -> [['A '], ['string', ' with '], ['variables', ' and '], ['trailing', ' words.']]
    .map(([$, ...$$]) => ($$.length ? [[$] as [variable: string], ...$$] : $))
    // -> ['A ', [['string'], ' with '], [['variables'], ' and '], [['trailing'], ' words.']]
    .flat()
    // -> ['A ', ['string'], ' with ', ['variables'], ' and ', ['trailing'], ' words.']
    .reduce<
      | { nodes: ReactNode[]; hasReactNodes: true }
      | { nodes: string[]; hasReactNodes: false }
    >(
      (acc, token) => {
        if (typeof token === 'string') {
          acc.nodes.push(token)
        } else {
          const [key] = token
          const keyUnsafe = key as keyof typeof variables
          const variable = variables?.[keyUnsafe]
          const nodesUnsafe = acc.nodes as ReactNode[]
          nodesUnsafe.push(variable)
          if (typeof variable !== 'string') {
            acc.hasReactNodes = true
          }
        }
        return acc
      },
      { nodes: [], hasReactNodes: false }
    )
  const valueInterpolated = fragments.hasReactNodes
    ? fragments.nodes
        // To silence sonar/eslint: don't use index as key.
        .map((node, index) => [`${index}:${node}`, node] as [string, ReactNode])
        .map(([id, node]) => <Fragment key={id}>{node}</Fragment>)
    : fragments.nodes.join('')
  const valueInterpolatedUnsafe = valueInterpolated
  return valueInterpolatedUnsafe
}
